import {Filter, PlanFilterColumn} from "../reducers/App/state";
import {Currency, default as S} from "../core/reducers/Finance/state";
import * as C from "../core/util/constants";
import {getFXValue} from "../core/util/currency";
import {getPeriodGroupForProject, getPeriodStartEndForProject} from "../core/util/finance-planning";

export function getScrollBarWidth(): number {
  const {document} = window;
  
  // Create the measurement node
  const scrollDiv = document.createElement("div");
  scrollDiv.style.width = "100px";
  scrollDiv.style.height = "100px";
  scrollDiv.style.overflow = "scroll";
  scrollDiv.style.position = "absolute";
  scrollDiv.style.top = "-100px";
  document.body.appendChild(scrollDiv);

  // Get the scrollbar width
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

  // Delete the DIV 
  document.body.removeChild(scrollDiv);
  
  return scrollbarWidth;
}

export function fromYen(value: number, currency: Currency): number {
  return getFXValue(value, C.CURRENCY_JPY.id, currency.id, null);
}

export function toYen(value: number, currency: Currency): number {
  return getFXValue(value, currency.id, C.CURRENCY_JPY.id, null);
}

export function shuffleArray(array: any[]): any[] {
  let m = array.length;
  let t;
  let i;
  
  // While there remain elements to shuffle…
  while (m) {
    
    // Pick a remaining element…
    i = Math.floor(Math.random() * m--);
    
    // And swap it with the current element.
    t = array[m];
    array[m] = array[i];
    array[i] = t;
  }
  
  return array;
}

export function pseudoGaussianRandom(mean: number, min: number, max: number): number {
  let u = 0;
  let v = 0;
  while(u === 0){
    u = Math.random(); // Converting [0,1) to (0,1)
  }
  while(v === 0){
    v = Math.random();
  }
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  
  num = num / 3;
  if(num > 1 || num < -1){
    return pseudoGaussianRandom(mean, min, max);
  }
  
  let variant;
  if((num + 1)/2 > (mean - min)/(max - min)){
    variant = max - mean;
  }
  else{
    variant = mean - min;
  }
  num = mean + variant*num; // Standard normal to Non-Standard normal (mean is not 0 and variant not 1)
  if(num < min || num > max){
    return pseudoGaussianRandom(mean, min, max);
  }
  
  return num;
}

export function getDaysInMonth(date: Date): number {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth()+1);
  d.setDate(0);
  return d.getDate();
}

export function getTextContainerSize(text: string, fontSize: number): {width: number, height: number} {
  const {document} = window;
  
  const div = document.createElement("div");
  div.style.fontSize = fontSize + "px";
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.height = "auto";
  div.style.width = "auto";
  div.style.whiteSpace = "nowrap";
  div.style.top = "-1000px";
  div.innerText = text;
  document.body.appendChild(div);
  
  const height = div.clientHeight + 1;
  const width = div.clientWidth + 1;
  
  // Delete the DIV 
  document.body.removeChild(div);
  
  return {height, width};
}

// From https://github.com/handsontable/handsontable/blob/f2310fd4f6cf6b3c00c892a176f90320ac785251/src/helpers/feature.js
// MIT License
const hasCaptionProblem = (() => {
  let _hasCaptionProblem: boolean | undefined;
  
  function detectCaptionProblem() {
    if(_hasCaptionProblem !== void 0){
      return _hasCaptionProblem;
    }
    
    const TABLE = document.createElement("TABLE");
    TABLE.style.borderSpacing = "0";
    TABLE.style.borderWidth = "0";
    TABLE.style.padding = "0";
    const TBODY = document.createElement("TBODY");
    TABLE.appendChild(TBODY);
    const TR = document.createElement("TR");
    TBODY.appendChild(TR);
    const TD = document.createElement("TD");
    TR.appendChild(TD);
    TD.innerHTML = "<tr><td>t<br>t</td></tr>";
    
    const CAPTION = document.createElement("CAPTION");
    CAPTION.innerHTML = "c<br>c<br>c<br>c";
    CAPTION.style.padding = "0";
    CAPTION.style.margin = "0";
    TABLE.insertBefore(CAPTION, TBODY);
    
    document.body.appendChild(TABLE);
    _hasCaptionProblem = (TABLE.offsetHeight < 2 * TBODY.offsetHeight); // boolean
    document.body.removeChild(TABLE);
    return _hasCaptionProblem;
  }
  
  return detectCaptionProblem;
})();


/**
 * From https://github.com/handsontable/handsontable/blob/f2310fd4f6cf6b3c00c892a176f90320ac785251/src/helpers/dom/element.js
 * @param elem
 * @returns {number} Element's outer height
 */
function getOuterHeight(elem: HTMLElement){
  if (hasCaptionProblem() && elem.firstChild && elem.firstChild.nodeName === "CAPTION") {
    // fixes problem with Firefox ignoring <caption> in TABLE.offsetHeight
    // jQuery (1.10.1) still has this unsolved
    // may be better to just switch to getBoundingClientRect
    // http://bililite.com/blog/2009/03/27/finding-the-size-of-a-table/
    // http://lists.w3.org/Archives/Public/www-style/2009Oct/0089.html
    // http://bugs.jquery.com/ticket/2196
    // http://lists.w3.org/Archives/Public/www-style/2009Oct/0140.html#start140
    return elem.offsetHeight + (elem.firstChild as HTMLElement).offsetHeight;
  }
  
  return elem.offsetHeight;
}

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
export function getContainerHeight(element: HTMLElement){
  const elem = element.cloneNode(true) as HTMLElement;
  elem.style.position = "absolute";
  elem.style.top = "-2000px";
  window.document.body.appendChild(elem);
  const height = getOuterHeight(elem);
  window.document.body.removeChild(elem);
  return height;
}

export function shouldSidebarClosed(activity: string, isLoggedIn: boolean){
  if(process.env.REACT_APP_SHOW_SIDEBAR_AT_WELCOME_PAGE){
    return false;
  }
  
  if(isLoggedIn){
    return activity === "welcome";
  }
  
  return true;
}

type ApplyFilterFunc = (
  projects: S.PlanProject[],
  periodGroups: S.PlanPeriodGroup[],
  filterRules: Array<Filter<PlanFilterColumn>> | undefined,
) => S.PlanProject[];
/**
 * @param {array} projects
 * @param {array} periodGroups
 * @param {array} filterRules
 * @returns {Object}
 */
export const applyFilter: ApplyFilterFunc = (projects, periodGroups, filterRules) => {
  if (!Array.isArray(filterRules) || filterRules.length < 1) {
    return projects;
  }
  
  return projects.filter(project => {
    return filterRules.every(f => {
      const {rule, values, column} = f;
      let value_to_test;
      
      if (["periodEnd", "periodGroupName", "periodGroupType"].includes(column)) {
        const periodGroup = getPeriodGroupForProject(periodGroups, project);
        
        if (!periodGroup) {
          return false;
        }
        
        if (column === "periodGroupName") {
          value_to_test = periodGroup.name;
        } else if (column === "periodGroupType") {
          value_to_test = periodGroup.type;
        } else if (column === "periodEnd") {
          const {periodEnd} = getPeriodStartEndForProject(periodGroup, project);
          value_to_test = periodEnd;
        }
      } else {
        const projectColumn = column as keyof S.PlanProject;
        value_to_test = project[projectColumn];
      }
      
      switch (rule) {
        case "equalTo": {
          return value_to_test === values;
        }
        case "like": {
          return typeof (value_to_test) === "string" && new RegExp(values).test(value_to_test);
        }
        case "notLike": {
          return typeof (value_to_test) === "string" && !(new RegExp(values).test(value_to_test));
        }
        case "largerThan": {
          return (value_to_test as number) > values;
        }
        case "largerThanOET": {
          return (value_to_test as number) >= values;
        }
        case "lessThan": {
          return (value_to_test as number) < values;
        }
        case "lessThanOET": {
          return (value_to_test as number) <= values;
        }
        case "between": {
          return values[0] <= (value_to_test as number) && (value_to_test as number) <= values[1];
        }
        case "oneOf": {
          return values.includes(value_to_test);
        }
        case "notAnyOf": {
          return !values.includes(value_to_test);
        }
        case "notEqualTo": {
          return value_to_test !== values;
        }
        case "isEmpty": {
          return value_to_test === "" || value_to_test === null || typeof (value_to_test) === "undefined";
        }
        default:
          return true;
      }
    });
  });
};
